import { useState, useEffect, useCallback } from "react";
import api from "../Services/api";
import { useAuth } from "../Contexts/AuthContext";
import "../styles/leave.css";

const getLeaveTypeStyle = (name) => {
  const defaults = {
    "casual":    { bg:"#e8f4fd", color:"#2a85c7", icon:"fa-mug-hot" },
    "sick":      { bg:"#fef2f2", color:"#c0392b", icon:"fa-kit-medical" },
    "earned":    { bg:"#edf7ee", color:"#2a7a38", icon:"fa-briefcase" },
    "maternity": { bg:"#f3e8ff", color:"#7c3aed", icon:"fa-baby" },
    "emergency": { bg:"#fff8e6", color:"#c47f0a", icon:"fa-triangle-exclamation" },
    "unpaid":    { bg:"#f3f4f6", color:"#6b7280", icon:"fa-ban" },
  };
  const key = String(name || "").toLowerCase();
  return defaults[key] || { bg:"#f3f4f6", color:"#4b5563", icon:"fa-calendar-days" };
};

const STATUS_CFG = {
  Pending:  { bg:"#fff8e6", color:"#c47f0a", dot:"#e6a817" },
  Approved: { bg:"#edf7ee", color:"#2a7a38", dot:"#36a849" },
  Rejected: { bg:"#fef2f2", color:"#c0392b", dot:"#e05c5c" },
};

const AVATAR_COLORS = [
  ["#eef5d0","#4a6508"],["#e8f4fd","#2a85c7"],["#f2f7e4","#3a7000"],
  ["#fff8e6","#c47f0a"],["#fef2f2","#c0392b"],["#f3e8ff","#7c3aed"],
];



const EMPTY_FORM = { leave_type:"", start_date:"", end_date:"", reason:"" };

const initials  = (n="") => n.trim().split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()||"?";
const fmtDate   = (d)    => { if(!d) return "—"; const dt=new Date(d); return dt.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}); };


const DAY_NAMES = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function LeaveManagement() {
  const { user } = useAuth();
  const [leaves,        setLeaves]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState("All");
  const [search,        setSearch]        = useState("");
  const [typeFilter,    setTypeFilter]    = useState("All");
  const [modal,         setModal]         = useState(null);
  const [activeLeave,   setActiveLeave]   = useState(null);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [errors,        setErrors]        = useState({});
  const [toast,         setToast]         = useState("");
  const [rejReason,     setRejReason]     = useState("");
  const [rejError,      setRejError]      = useState("");
  const [dbLeaveTypes,  setDbLeaveTypes]  = useState([]);
  const [balances,      setBalances]      = useState([]);
  const [balSearch,     setBalSearch]     = useState("");
  const [activeViewTab, setActiveViewTab] = useState("my-balances");


  const canManage = user?.is_superuser || 
                   user?.all_permissions?.includes("manage_leaves") || 
                   user?.all_permissions?.includes("primary_org_admin");

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("hr/leaves/");
      setLeaves(response.data);
    } catch (error) {
      console.error("Failed to fetch leaves:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const response = await api.get("hr/leave-types/");
      setDbLeaveTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch leave types:", error);
    }
  }, []);

  const fetchBalances = useCallback(async () => {
    try {
      const response = await api.get("hr/leave-balances/");
      setBalances(response.data);
    } catch (error) {
      console.error("Failed to fetch leave balances:", error);
    }
  }, []);

  useEffect(() => { 
    fetchLeaves();
    fetchLeaveTypes();
    fetchBalances();
  }, [fetchLeaves, fetchLeaveTypes, fetchBalances]);

  const getSelectableLeaveTypes = useCallback(() => {
    const types = dbLeaveTypes.map(t => t.name);
    if (!types.includes("Unpaid")) {
      types.push("Unpaid");
    }
    return types;
  }, [dbLeaveTypes]);

  const showToast    = (msg)=>{ setToast(msg); setTimeout(()=>setToast(""),3200); };
  const closeModal   = ()=>{ setModal(null); setActiveLeave(null); setErrors({}); setRejReason(""); setRejError(""); };

  const TABS = ["All","Pending","Approved","Rejected", "My Leaves"];

  const filtered = leaves.filter(l=>{
    const q = search.toLowerCase();
    const empName = l.user_details?.name || l.user_details?.username || "";
    const matchSearch = empName.toLowerCase().includes(q) || (l.reason && l.reason.toLowerCase().includes(q));
    const matchTab = activeTab==="All" ? true : activeTab==="My Leaves" ? l.user === user.id : l.status===activeTab;
    const matchType = typeFilter==="All"||l.leave_type===typeFilter;
    return matchSearch&&matchTab&&matchType;
  });

  const counts = {
    total:    leaves.length,
    pending:  leaves.filter(l=>l.status==="Pending").length,
    approved: leaves.filter(l=>l.status==="Approved").length,
    rejected: leaves.filter(l=>l.status==="Rejected").length,
  };

  const validate = (f)=>{
    const e={};
    if(!f.start_date)          e.start_date="Start date required";
    if(!f.end_date)            e.end_date="End date required";
    else if(f.end_date < f.start_date) e.end_date="End date must be after start date";
    if(!f.reason.trim()) e.reason="Reason required";
    return e;
  };

  const getErrorMessage = (error, defaultMsg) => {
    if (error.response?.data) {
      const data = error.response.data;
      if (typeof data === "string") return data;
      if (Array.isArray(data)) return data.join(", ");
      if (typeof data === "object") {
        return Object.entries(data)
          .map(([key, val]) => {
            const fieldName = key === "non_field_errors" || key === "detail" ? "" : `${key}: `;
            const valMsg = Array.isArray(val) ? val.join(", ") : String(val);
            return `${fieldName}${valMsg}`;
          })
          .join(" | ");
      }
    }
    return defaultMsg;
  };

  const handleApply = async ()=>{
    const e=validate(form); if(Object.keys(e).length){setErrors(e);return;}
    try {
      await api.post("hr/leaves/", form);
      showToast("✅ Leave application submitted");
      fetchLeaves();
      fetchBalances();
      closeModal();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to submit application."));
    }
  };

  const handleApprove = async (l)=>{
    try {
      await api.post(`hr/leaves/${l.id}/approve/`, {});
      showToast(`✅ Leave approved for ${l.user_details.name}`);
      fetchLeaves();
      fetchBalances();
      closeModal();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to approve leave."));
    }
  };

  const handleRejectConfirm = async ()=>{
    if(!rejReason.trim()){setRejError("Please provide a rejection reason");return;}
    try {
      await api.post(`hr/leaves/${activeLeave.id}/reject/`, { rejection_reason: rejReason });
      showToast(`❌ Leave rejected for ${activeLeave.user_details.name}`);
      fetchLeaves();
      fetchBalances();
      closeModal();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to reject leave."));
    }
  };

  const handleDelete = async ()=>{
    try {
      await api.delete(`hr/leaves/${activeLeave.id}/`);
      showToast("🗑️ Leave record deleted");
      fetchLeaves();
      fetchBalances();
      closeModal();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to delete leave."));
    }
  };

  const openApply  = ()=>{
    const selectable = getSelectableLeaveTypes();
    setForm({ leave_type: selectable[0] || "Casual", start_date: "", end_date: "", reason: "" });
    setErrors({});
    setModal("apply");
  };
  const openView   = (l)=>{ setActiveLeave(l); setModal("view"); };
  const openApproveConfirm = (l)=>{ setActiveLeave(l); setModal("approveConfirm"); };
  const openReject = (l)=>{ setActiveLeave(l); setRejReason(l.rejection_reason||""); setRejError(""); setModal("reject"); };
  const openDelete = (l)=>{ setActiveLeave(l); setModal("delete"); };



  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"/>

      <div className="lv-root">
        <div className="lv-header">
          <div className="lv-title-block">
            <span className="lv-eyebrow">HR · Leave Management</span>
            <h1 className="lv-title">Leave Management</h1>
            <p className="lv-subtitle">{counts.pending} pending approval · {counts.approved} approved this cycle</p>
          </div>
          <div className="lv-header-btns">
            <button className="lv-apply-btn" onClick={openApply}>
              <i className="fa-solid fa-plus"/> Apply Leave
            </button>
          </div>
        </div>

        {toast&&<div className="lv-toast"><i className="fa-solid fa-circle-check"/>{toast}</div>}

        {/* Main Tab Switcher */}
        <div className="lv-tabs" style={{ marginBottom: "24px" }}>
          <button 
            className={`lv-tab${activeViewTab === "my-balances" ? " active" : ""}`} 
            onClick={() => setActiveViewTab("my-balances")}
          >
            <i className="fa-solid fa-user-check" style={{ marginRight: "6px" }} /> My Leave Balances
          </button>
          
          {canManage && (
            <button 
              className={`lv-tab${activeViewTab === "employee-balances" ? " active" : ""}`} 
              onClick={() => setActiveViewTab("employee-balances")}
            >
              <i className="fa-solid fa-users-gear" style={{ marginRight: "6px" }} /> Employee Leave Balances
            </button>
          )}

          <button 
            className={`lv-tab${activeViewTab === "leave-requests" ? " active" : ""}`} 
            onClick={() => setActiveViewTab("leave-requests")}
          >
            <i className="fa-solid fa-file-signature" style={{ marginRight: "6px" }} /> Leave Requests
          </button>
        </div>

        {/* Tab 1: My Leave Balances */}
        {activeViewTab === "my-balances" && (
          <div className="lv-balances-section" style={{ marginTop: 0 }}>
            <h2 className="lv-section-title">My Leave Balances</h2>
            <div className="lv-balances-grid">
              {balances.filter(b => b.user == user?.id).length === 0 && (
                <div className="lv-empty-balances" style={{ padding: "40px 24px", background: "#ffffff", border: "1.5px dashed var(--border)", borderRadius: "var(--r-md)", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", gridColumn: "1 / -1" }}>
                  <div style={{ width: "50px", height: "50px", borderRadius: "50%", background: "var(--green-lt)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--green)", fontSize: "20px" }}>
                    <i className="fa-solid fa-calendar-xmark" />
                  </div>
                  <div>
                    <h4 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: "var(--text)", fontSize: "14px", margin: "0 0 4px" }}>No Leave Balances Allocated</h4>
                    <p style={{ fontSize: "12px", color: "var(--text-soft)", margin: 0 }}>There are no leave types or balances assigned to your account yet.</p>
                    {canManage && (
                      <p style={{ fontSize: "11px", color: "var(--green)", marginTop: "8px", fontWeight: "700" }}>
                        💡 Tip: Visit the HR Settings page to configure leave categories.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {balances.filter(b => b.user == user?.id).map(b => {
                const style = getLeaveTypeStyle(b.leave_type_details?.name);
                const quota = parseFloat(b.leave_type_details?.quota || 0);
                const balance = parseFloat(b.balance);
                const used = Math.max(0, quota - balance);
                const pct = quota > 0 ? (balance / quota) * 100 : 0;
                return (
                  <div key={b.id} className="lv-balance-card" style={{ borderLeftColor: style.color, display: "flex", flexDirection: "column", gap: "16px", alignItems: "stretch", padding: "18px 20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                      <div className="lv-bal-left" style={{ gap: "12px" }}>
                        <div className="lv-bal-icon" style={{ background: style.bg, color: style.color, width: "42px", height: "42px", borderRadius: "12px", fontSize: "16px" }}>
                          <i className={`fa-solid ${style.icon}`} />
                        </div>
                        <div>
                          <h4 className="lv-bal-name" style={{ fontSize: "15px", fontWeight: "800", fontFamily: "'Nunito', sans-serif" }}>{b.leave_type_details?.name || "Leave"}</h4>
                          <span className="lv-bal-freq" style={{ fontSize: "9px", fontWeight: "800", color: "var(--text-soft)" }}>{b.leave_type_details?.frequency} CYCLE</span>
                        </div>
                      </div>
                      <div className="lv-bal-right" style={{ textAlign: "right" }}>
                        <div className="lv-bal-values" style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
                          <span className="lv-bal-rem" style={{ color: style.color, fontSize: "22px", fontWeight: "900", fontFamily: "'Nunito', sans-serif" }}>{balance}</span>
                          <span className="lv-bal-total" style={{ fontSize: "12px", color: "var(--text-soft)", fontWeight: "700" }}>/ {quota}d</span>
                        </div>
                        <span className="lv-bal-label" style={{ fontSize: "9px", fontWeight: "800", color: "var(--text-soft)", letterSpacing: "0.5px" }}>Remaining</span>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div style={{ width: "100%" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--text-soft)", marginBottom: "5px", fontWeight: "600" }}>
                        <span>{used}d used</span>
                        <span>{balance}d left</span>
                      </div>
                      <div style={{ width: "100%", height: "6px", background: "#f3f4f6", borderRadius: "10px", overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: style.color, borderRadius: "10px", transition: "width 0.3s" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab 2: Employee Leave Balances */}
        {canManage && activeViewTab === "employee-balances" && (
          <div className="lv-balances-section" style={{ marginTop: 0 }}>
            <div className="lv-section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
              <h2 className="lv-section-title" style={{ marginBottom: 0 }}>Employee Leave Balances</h2>
              <div className="lv-search-wrap" style={{ maxWidth: "300px", flex: 1, margin: 0 }}>
                <div className="lv-search-icon"><i className="fa-solid fa-magnifying-glass"/></div>
                <input 
                  className="lv-search" 
                  placeholder="Search employee or type..." 
                  value={balSearch} 
                  onChange={e => setBalSearch(e.target.value)}
                />
              </div>
            </div>
            
            <div className="lv-table-card">
              <div className="lv-table-wrap">
                <table className="lv-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Leave Type</th>
                      <th>Cycle</th>
                      <th>Allocated Quota</th>
                      <th>Remaining Balance</th>
                      <th>Used Leaves</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balances.filter(b => b.user != user?.id).filter(b => {
                      const q = balSearch.toLowerCase();
                      const name = b.user_details?.name || b.user_details?.username || "";
                      const type = b.leave_type_details?.name || "";
                      return name.toLowerCase().includes(q) || type.toLowerCase().includes(q);
                    }).length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: "center", padding: "50px 20px" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "var(--green-xlt)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-soft)", fontSize: "18px" }}>
                              <i className="fa-solid fa-users-slash" />
                            </div>
                            <div>
                              <h4 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: "var(--text)", fontSize: "14px", margin: "0 0 4px" }}>No Employee Leave Balances</h4>
                              <p style={{ fontSize: "12px", color: "var(--text-soft)", margin: 0 }}>No other employee leave allocations match the criteria or exist in this organization.</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      balances.filter(b => b.user != user?.id).filter(b => {
                        const q = balSearch.toLowerCase();
                        const name = b.user_details?.name || b.user_details?.username || "";
                        const type = b.leave_type_details?.name || "";
                        return name.toLowerCase().includes(q) || type.toLowerCase().includes(q);
                      }).map((b, i) => {
                        const style = getLeaveTypeStyle(b.leave_type_details?.name);
                        const [abg, acolor] = AVATAR_COLORS[i % AVATAR_COLORS.length];
                        const quota = parseFloat(b.leave_type_details?.quota || 0);
                        const balance = parseFloat(b.balance);
                        const used = Math.max(0, quota - balance);
                        const usedPct = quota > 0 ? (used / quota) * 100 : 0;
                        return (
                          <tr key={b.id}>
                            <td>
                              <div className="lv-cell-emp">
                                <div className="lv-avatar" style={{ background: abg, color: acolor }}>
                                  {initials(b.user_details?.name || b.user_details?.username)}
                                </div>
                                <div>
                                  <div className="lv-emp-name">{b.user_details?.name || b.user_details?.username}</div>
                                  <div className="lv-emp-dept">{b.user_details?.department || "N/A"}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="lv-type-badge" style={{ background: style.bg, color: style.color }}>
                                <i className={`fa-solid ${style.icon}`} style={{ fontSize: 11 }} /> {b.leave_type_details?.name || "Leave"}
                              </span>
                            </td>
                            <td>
                              <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-soft)", textTransform: "uppercase" }}>{b.leave_type_details?.frequency}</span>
                            </td>
                            <td>
                              <span className="lv-days-badge">{quota} days</span>
                            </td>
                            <td>
                              <span className="lv-days-badge" style={{ color: style.color, fontWeight: "800" }}>{balance} days</span>
                            </td>
                            <td>
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "120px", maxWidth: "160px" }}>
                                <span className="lv-days-badge" style={{ fontSize: "11px", fontWeight: "600" }}>{used} / {quota} days used</span>
                                <div style={{ width: "100%", height: "6px", background: "#f3f4f6", borderRadius: "10px", overflow: "hidden" }}>
                                  <div style={{ width: `${usedPct}%`, height: "100%", background: style.color, borderRadius: "10px", transition: "width 0.3s" }} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: Leave Requests */}
        {activeViewTab === "leave-requests" && (
          <>
            <div className="lv-stats">
              <div className="lv-stat-card">
                <div className="lv-stat-ico" style={{background:"#eef5d0"}}><i className="fa-solid fa-calendar-days" style={{color:"#4a6508"}}/></div>
                <div><div className="lv-stat-num">{counts.total}</div><div className="lv-stat-lbl">Total Requests</div></div>
              </div>
              <div className="lv-stat-card">
                <div className="lv-stat-ico" style={{background:"#fff8e6"}}><i className="fa-solid fa-clock" style={{color:"#c47f0a"}}/></div>
                <div><div className="lv-stat-num">{counts.pending}</div><div className="lv-stat-lbl">Pending</div></div>
              </div>
              <div className="lv-stat-card">
                <div className="lv-stat-ico" style={{background:"#edf7ee"}}><i className="fa-solid fa-circle-check" style={{color:"#2a7a38"}}/></div>
                <div><div className="lv-stat-num">{counts.approved}</div><div className="lv-stat-lbl">Approved</div></div>
              </div>
              <div className="lv-stat-card">
                <div className="lv-stat-ico" style={{background:"#fef2f2"}}><i className="fa-solid fa-circle-xmark" style={{color:"#c0392b"}}/></div>
                <div><div className="lv-stat-num">{counts.rejected}</div><div className="lv-stat-lbl">Rejected</div></div>
              </div>
            </div>

            <div className="lv-tabs">
              {TABS.map(t=>(
                <button key={t} className={`lv-tab${activeTab===t?" active":""}`} onClick={()=>setActiveTab(t)}>{t}</button>
              ))}
            </div>

            <div className="lv-toolbar">
              <div className="lv-search-wrap">
                <div className="lv-search-icon"><i className="fa-solid fa-magnifying-glass"/></div>
                <input className="lv-search" placeholder="Search employee, reason…" value={search} onChange={e=>setSearch(e.target.value)}/>
              </div>
              <select className="lv-filter-select" value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
                <option value="All">All Types</option>
                {getSelectableLeaveTypes().map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="lv-table-card">
              <div className="lv-table-wrap">
                <table className="lv-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Type</th>
                      <th>Duration</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th style={{textAlign:"right"}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="7" style={{textAlign:"center", padding: "40px"}}>Loading...</td></tr>
                    ) : filtered.length===0?(
                      <tr><td colSpan="7"><div className="lv-empty">No leave requests found</div></td></tr>
                    ):filtered.map((l,i)=>{
                      const sc=STATUS_CFG[l.status]||STATUS_CFG.Pending;
                      const tc=getLeaveTypeStyle(l.leave_type);
                      const [abg,acolor]=AVATAR_COLORS[i%AVATAR_COLORS.length];
                      const isPend=l.status==="Pending";
                      return (
                        <tr key={l.id}>
                          <td>
                            <div className="lv-cell-emp">
                              <div className="lv-avatar" style={{background:abg,color:acolor}}>{initials(l.user_details?.name || l.user_details?.username)}</div>
                              <div>
                                <div className="lv-emp-name">{l.user_details?.name || l.user_details?.username}</div>
                                <div className="lv-emp-dept">{l.user_details?.department || "N/A"}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="lv-type-badge" style={{background:tc.bg,color:tc.color}}>
                              <i className={`fa-solid ${tc.icon}`} style={{fontSize:11}}/>{l.leave_type}
                            </span>
                          </td>
                          <td>{fmtDate(l.start_date)} — {fmtDate(l.end_date)}</td>
                          <td><span className="lv-days-badge">{l.days}d</span></td>
                          <td>
                            <span className="lv-status-badge" style={{background:sc.bg,color:sc.color}}>
                              <span className="lv-status-dot" style={{background:sc.dot}}/>{l.status}
                            </span>
                          </td>
                          <td><span className="lv-reason-text" title={l.reason}>{l.reason}</span></td>
                          <td>
                            <div className="lv-actions">
                              {canManage && isPend && (
                                  <>
                                      <button className="lv-action-btn approve" onClick={()=>openApproveConfirm(l)}><i className="fa-solid fa-check"/></button>
                                      <button className="lv-action-btn reject" onClick={()=>openReject(l)}><i className="fa-solid fa-xmark"/></button>
                                  </>
                              )}
                              <button className="lv-action-btn" onClick={()=>openView(l)}><i className="fa-solid fa-eye"/></button>
                              {(canManage || l.user === user.id) && (
                                  <button className="lv-action-btn" onClick={()=>openDelete(l)}><i className="fa-solid fa-trash"/></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>

      {modal==="apply"&&(
        <div className="lv-modal-backdrop" onClick={closeModal}>
          <div className="lv-modal lv-modal--wide" onClick={e=>e.stopPropagation()}>
            <div className="lv-modal-head">
              <h2 className="lv-modal-title">Apply for Leave</h2>
              <button className="lv-modal-close" onClick={closeModal}><i className="fa-solid fa-xmark"/></button>
            </div>
            <div className="lv-modal-body">
              <div className="lv-form-field">
                <label className="lv-form-label">Leave Type</label>
                <div className="lv-leave-type-picker">
                  {getSelectableLeaveTypes().map(t=>{
                    const cfg = getLeaveTypeStyle(t);
                    return (
                      <button key={t} className={`lv-type-opt${form.leave_type===t?" sel":""}`}
                        style={form.leave_type===t?{background:cfg.bg,color:cfg.color,borderColor:cfg.color}:{}}
                        onClick={()=>setForm({...form,leave_type:t})}>{t}</button>
                    );
                  })}
                </div>
              </div>
              <div className="lv-form-row">
                <div className="lv-form-field">
                  <label className="lv-form-label">Start Date</label>
                  <input className="lv-form-input" type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})}/>
                  {errors.start_date&&<span className="lv-form-error">{errors.start_date}</span>}
                </div>
                <div className="lv-form-field">
                  <label className="lv-form-label">End Date</label>
                  <input className="lv-form-input" type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})}/>
                  {errors.end_date&&<span className="lv-form-error">{errors.end_date}</span>}
                </div>
              </div>
              <div className="lv-form-field">
                <label className="lv-form-label">Reason</label>
                <textarea className="lv-form-input lv-textarea" rows={3} value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})}/>
                {errors.reason&&<span className="lv-form-error">{errors.reason}</span>}
              </div>
            </div>
            <div className="lv-modal-foot">
              <button className="lv-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="lv-btn-save" onClick={handleApply}>Submit Application</button>
            </div>
          </div>
        </div>
      )}

      {modal==="view" && activeLeave && (
          <div className="lv-modal-backdrop" onClick={closeModal}>
            <div className="lv-modal" onClick={e=>e.stopPropagation()}>
                <div className="lv-modal-head">
                    <h2 className="lv-modal-title">Leave Details</h2>
                    <button className="lv-modal-close" onClick={closeModal}><i className="fa-solid fa-xmark"/></button>
                </div>
                <div className="lv-modal-body">
                    <p><strong>Employee:</strong> {activeLeave.user_details.name}</p>
                    <p><strong>Type:</strong> {activeLeave.leave_type}</p>
                    <p><strong>Dates:</strong> {fmtDate(activeLeave.start_date)} - {fmtDate(activeLeave.end_date)} ({activeLeave.days} days)</p>
                    <p><strong>Status:</strong> {activeLeave.status}</p>
                    <p><strong>Reason:</strong> {activeLeave.reason}</p>
                    {activeLeave.status === "Rejected" && <p style={{color:"red"}}><strong>Rejection Reason:</strong> {activeLeave.rejection_reason}</p>}
                </div>
                <div className="lv-modal-foot">
                    <button className="lv-btn-cancel" onClick={closeModal}>Close</button>
                    {canManage && activeLeave.status === "Pending" && (
                        <>
                            <button className="lv-btn-reject" onClick={()=>openReject(activeLeave)}>Reject</button>
                            <button className="lv-btn-approve" onClick={()=>handleApprove(activeLeave)}>Approve</button>
                        </>
                    )}
                </div>
            </div>
          </div>
      )}

      {modal==="approveConfirm"&&activeLeave&&(
        <div className="lv-modal-backdrop" onClick={closeModal}>
          <div className="lv-modal lv-modal--sm" onClick={e=>e.stopPropagation()}>
            <div className="lv-reject-body">
              <h2 className="lv-reject-title">Approve Leave?</h2>
              <p>Approve leave for {activeLeave.user_details.name}?</p>
            </div>
            <div className="lv-modal-foot">
              <button className="lv-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="lv-btn-approve" onClick={()=>handleApprove(activeLeave)}>Approve</button>
            </div>
          </div>
        </div>
      )}

      {modal==="reject"&&activeLeave&&(
        <div className="lv-modal-backdrop" onClick={closeModal}>
          <div className="lv-modal lv-modal--sm" onClick={e=>e.stopPropagation()}>
            <h2 className="lv-reject-title">Reject Leave</h2>
            <textarea className="lv-form-input" placeholder="Rejection reason..." value={rejReason} onChange={e=>setRejReason(e.target.value)}/>
            {rejError&&<span className="lv-form-error">{rejError}</span>}
            <div className="lv-modal-foot">
              <button className="lv-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="lv-btn-reject" onClick={handleRejectConfirm}>Reject</button>
            </div>
          </div>
        </div>
      )}

      {modal==="delete"&&activeLeave&&(
        <div className="lv-modal-backdrop" onClick={closeModal}>
          <div className="lv-modal lv-modal--sm" onClick={e=>e.stopPropagation()}>
            <h2 className="lv-reject-title">Delete Record?</h2>
            <p>Permanently delete this leave request?</p>
            <div className="lv-modal-foot">
              <button className="lv-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="lv-btn-reject" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}